import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/ToastContext';
import PageBar, { SearchInput } from '../components/PageBar';
import ListGrid from '../components/ListGrid';
import Pill from '../components/Pill';
import ItemPanel from './catalog/ItemPanel';
import { revealPanel } from '../components/ListPanel';
import { loadItem, loadPage, findByBarcode, saveBarcodes, BARCODE_FIELDS } from './catalog/catalogData';
import { PAGE, loadOrgPage, loadVendorPage, loadVendors, loadOrgCounts } from '../lib/orgCatalog';
import { downloadCsv } from './reports/reportData';
import { recordExport } from '../lib/referenceExports';
import { useAuth } from '../components/AuthContext';
import { createPortal } from 'react-dom';
import PageLoading from '../components/PageLoading';
import { pressable } from '../lib/pressable';
import ConversionsTab from './admin/Ordering/ConversionsTab';



// -- Registering a barcode ------------------------------------------------

/*
  **Two comment lines here held 207 KB of garbled symbols until 11 September
  2026**, phase 6 of the brand rollout. Where a row of dashes used to be, one of
  them had grown to 198,472 bytes and the other to 8,767: the same few
  characters re-encoded from UTF-8 into Windows-1252 and back, over and over,
  each pass multiplying them. **They were 84 percent of this file.**

  Nothing on any screen was ever wrong because of it, and no test ever failed.
  It made this file 246 KB instead of 39 KB, which every session that opened it
  paid for, and it broke the brand guide's rule 12, which asks for plain ASCII
  in a delivered file.

  **The cause is worth knowing, because it is the same fault OnHand guards
  against elsewhere.** `src/lib/catalogParse.js` watches for exactly this when
  it reads an uploaded catalog: a file saved as Windows-1252 and read as UTF-8.
  The guard is there for other people's files and there was none for our own.
*/
// Every slot, empty. One list, so a slot added to the database and to
// BARCODE_FIELDS turns up here without this file being touched.
const emptySlots = () => Object.fromEntries(BARCODE_FIELDS.map(f => [f, '']));

export function BarcodeRegisterModal({ onClose, onSaved, scanFirst = false }) {
  const toast = useToast();
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch]             = useState('');
  const [barcodes, setBarcodes]         = useState(emptySlots());
  const [scanning, setScanning]         = useState(false);
  const [lookupState, setLookupState]   = useState(null); // 'scanning'|'notfound'|null
  const [notFoundBarcode, setNotFoundBarcode] = useState('');
  const [saving, setSaving]             = useState(false);
  const [scanTarget, setScanTarget]     = useState(null); // which slot is being scanned
  const scannerRef    = useRef(null);
  const processingRef = useRef(false);

  /*
    **This used to be handed the whole catalog and filter it here.** It cannot
    be any more, and that is the point of job 13: the screen holds fifty items
    at a time now, not five thousand, so a search for something on page nineteen
    would have found nothing and said "no items match", which is a lie with a
    calm face on it. It asks the database, which is what the rest of this screen
    does, and takes the twenty it was already taking.
  */
  const [foundItems, setFoundItems] = useState([]);
  useEffect(() => {
    let live = true;
    if (!search.trim()) { setFoundItems([]); return undefined; }
    const t = setTimeout(async () => {
      const { rows } = await loadPage({ search, from: 0, size: 20 });
      if (live) setFoundItems(rows);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [search]);
  const filteredItems = foundItems;

  /*
    **The row from the list does not carry the barcodes**, because the list asks
    for the seven columns it draws rather than all fifty-six. So the whole row is
    read here, once, for the one item somebody picked. Without it every slot
    would have drawn empty on an item that already has barcodes, which reads as
    "none registered" and is not.
  */
  async function selectItem(picked, autoFillBarcode = '') {
    setSearch('');
    const { item: full } = await loadItem(picked.id);
    const item = full || picked;
    setSelectedItem(item);
    const slots = Object.fromEntries(BARCODE_FIELDS.map(f => [f, item[f] || '']));
    // If a scanned barcode should be auto-filled, put it in the first empty slot
    if (autoFillBarcode) {
      const emptySlot = BARCODE_FIELDS
        .find(f => !slots[f]);
      if (emptySlot) slots[emptySlot] = autoFillBarcode;
    }
    setBarcodes(slots);
  }

  async function startScan(slot) {
    // Pre-check permission state before opening scanner UI
    try {
      const permStatus = await navigator.permissions.query({ name: 'camera' });
      if (permStatus.state === 'denied') {
        toast.error('Camera access is blocked for this site. Open your browser settings, find this site, and allow camera access, then try again.', { duration: 8000 });
        return;
      }
    } catch (_) { /* permissions API not supported here, which is fine: the copy still works */ }

    setScanTarget(slot);
    setScanning(true);
    processingRef.current = false;

    const waitForEl = (id, timeout = 3000) => new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const el = document.getElementById(id);
        if (el) return resolve(el);
        if (Date.now() - start > timeout) return reject(new Error('Timeout waiting for scanner element'));
        setTimeout(check, 80);
      };
      check();
    });

    (async () => {
      try {
        await waitForEl('reg-qr-reader');
        const DBR = await import('dynamsoft-barcode-reader-bundle');
        DBR.CoreModule.engineResourcePaths.rootDirectory = 'https://cdn.jsdelivr.net/npm/';
        await DBR.LicenseManager.initLicense('DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA1NDAzMjIyLU1UQTFOREF6TWpJeUxYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDU0MDMyMjIiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjoxODUwNzU4NjAxfQ==', true);

        const { CameraEnhancer, CameraView, CaptureVisionRouter } = DBR;

        const container = document.getElementById('reg-qr-reader');
        if (!container) throw new Error('Scanner container not found');
        container.innerHTML = '';
        const view = await CameraView.createInstance();
        view.setVideoFit('cover');
        const uiEl = view.getUIElement();
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:relative;width:100%;height:260px;overflow:hidden;border-radius:10px;';
        uiEl.style.cssText = 'position:absolute;top:-46px;left:0;width:100%;height:calc(100% + 46px);';
        wrapper.appendChild(uiEl);
        container.appendChild(wrapper);
        const enhancer = await CameraEnhancer.createInstance(view);
        const router = await CaptureVisionRouter.createInstance();
        router.setInput(enhancer);

        router.addResultReceiver({
          onDecodedBarcodesReceived: (result) => {
            if (result?.barcodeResultItems?.length > 0 && !processingRef.current) {
              processingRef.current = true;
              const text = result.barcodeResultItems[0].text.trim();
              router.stopCapturing();
              enhancer.dispose();
              scannerRef.current = null;
              setBarcodes(prev => ({ ...prev, [slot]: text }));
              setScanning(false);
              setScanTarget(null);
              toast.success('Barcode scanned: ' + text);
            }
          }
        });

        scannerRef.current = { router, enhancer };
        await enhancer.open();
        await router.startCapturing('ReadSingleBarcode');
      } catch(err) {
        const msg = (err?.message || '').toLowerCase();
        let userMsg = 'Could not start camera. Close other apps using the camera and try again.';
        if (msg.includes('notallowed') || msg.includes('permission') || msg.includes('denied')) {
          userMsg = 'Camera permission denied. Allow camera access in your browser settings and try again.';
        } else if (msg.includes('notfound')) {
          userMsg = 'No camera found on this device.';
        }
        toast.error(userMsg, { duration: 7000 });
        setScanning(false);
        setScanTarget(null);
      }
    })();
  }

  async function startLookupScan() {
    setLookupState('scanning');
    processingRef.current = false;

    const waitForEl = (id, timeout = 3000) => new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const el = document.getElementById(id);
        if (el) return resolve(el);
        if (Date.now() - start > timeout) return reject(new Error('Timeout waiting for scanner element'));
        setTimeout(check, 80);
      };
      check();
    });

    (async () => {
      try {
        await waitForEl('lookup-qr-reader');
        const DBR = await import('dynamsoft-barcode-reader-bundle');
        DBR.CoreModule.engineResourcePaths.rootDirectory = 'https://cdn.jsdelivr.net/npm/';
        await DBR.LicenseManager.initLicense('DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA1NDAzMjIyLU1UQTFOREF6TWpJeUxYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDU0MDMyMjIiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjoxODUwNzU4NjAxfQ==', true);
        const { CameraEnhancer, CameraView, CaptureVisionRouter } = DBR;
        const container = document.getElementById('lookup-qr-reader');
        if (!container) throw new Error('Scanner container not found');
        container.innerHTML = '';
        const view = await CameraView.createInstance();
        view.setVideoFit('cover');
        const uiEl = view.getUIElement();
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:relative;width:100%;height:260px;overflow:hidden;border-radius:10px;';
        uiEl.style.cssText = 'position:absolute;top:-46px;left:0;width:100%;height:calc(100% + 46px);';
        wrapper.appendChild(uiEl);
        container.appendChild(wrapper);
        const enhancer = await CameraEnhancer.createInstance(view);
        const router = await CaptureVisionRouter.createInstance();
        router.setInput(enhancer);
        router.addResultReceiver({
          onDecodedBarcodesReceived: (result) => {
            if (result?.barcodeResultItems?.length > 0 && !processingRef.current) {
              processingRef.current = true;
              const text = result.barcodeResultItems[0].text.trim();
              router.stopCapturing();
              enhancer.dispose();
              scannerRef.current = null;
              handleLookupResult(text);
            }
          }
        });
        scannerRef.current = { router, enhancer };
        await enhancer.open();
        await router.startCapturing('ReadSingleBarcode');
      } catch(err) {
        toast.error('Camera error: ' + (err?.message || 'unknown') + ' (' + (err?.name || '') + ')', { duration: 10000 });
        setLookupState(null); // null = fall through to search UI, not idle (avoids retry loop)
      }
    })();
  }

  // Asked of the database: see findByBarcode (issue 13, 18 September 2026).
  async function handleLookupResult(barcode) {
    const { item: match, error } = await findByBarcode(barcode);
    if (error) {
      toast.error('Could not look that barcode up: ' + error.message);
      setLookupState(null);
      processingRef.current = false;
      return;
    }
    if (match) {
      toast.success('Item found: ' + match.item_number);
      selectItem(match); // barcode already registered on this item, so no auto-fill needed
      setLookupState(null);
    } else {
      setNotFoundBarcode(barcode);
      setLookupState('notfound');
    }
    processingRef.current = false;
  }

  function stopScan() {
    if (scannerRef.current) {
      try { scannerRef.current.router.stopCapturing(); } catch(e) {}
      try { scannerRef.current.enhancer.dispose(); } catch(e) {}
      scannerRef.current = null;
    }
    setScanning(false);
    setScanTarget(null);
    processingRef.current = false;
  }

  /*
    **A box emptied with its X removes that barcode, and a barcode already on
    another item is refused**: the owner's answers of 18 September 2026
    (question 18). This used to write only the boxes with something in them, so
    an emptied box kept its barcode and nothing could ever take one off, and it
    never looked at other items. The rules live in `saveBarcodes`.
  */
  async function handleSave() {
    if (!selectedItem) return;
    setSaving(true);
    const { ok, error, clash, nothing } = await saveBarcodes(selectedItem, barcodes);
    if (nothing) { toast.error('Nothing has changed.'); setSaving(false); return; }
    if (clash) {
      toast.error('Barcode ' + clash.code + ' is already on item ' + clash.item_number
        + (clash.description ? ' (' + clash.description + ')' : '') + '. One barcode belongs to one item.');
      setSaving(false);
      return;
    }
    if (error) { toast.error('Save failed: ' + error.message); setSaving(false); return; }
    if (!ok) { toast.error('Nothing was saved. You may not have permission to change the catalog.'); setSaving(false); return; }
    toast.success('Barcodes saved for ' + selectedItem.item_number);
    onSaved();
    // Reset for next item
    setSelectedItem(null);
    setBarcodes(emptySlots());
    setSaving(false);
  }

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try { scannerRef.current.router.stopCapturing(); } catch(e) {}
        try { scannerRef.current.enhancer.dispose(); } catch(e) {}
      }
    };
  }, []);

  // Auto-start lookup scan once on mount when scanFirst=true
  // startLookupScan() sets lookupState='scanning' which renders the lookup-qr-reader div,
  // then waitForEl polls until that div exists before initializing the camera.
  useEffect(() => {
    if (scanFirst) startLookupScan();
  }, []); // eslint-disable-line

  // The one list, shared with both scanners, so the boxes on this window and
  // the columns the database has cannot drift apart (question 25).

  /*
    **Handed to the page itself, not drawn where it was opened from.** Added 7
    September 2026, the same fix the shared dialog shell got the day before, and
    for the same reason: every card in OnHand is glass, and a browser measures
    anything pinned inside a blurring card against that card rather than against
    the screen. So this, opened from inside the catalog's card, was pinned to the
    corners of the card, and below 1024 it could land well below the bottom of
    the screen.

    **The fix and not a redesign.** The build order says the scanning dialogs get
    tokens and nothing more, because they are expected to be thrown away when
    scanning moves to a real phone app. Where a dialog lands is not a matter of
    style.
  */
  return createPortal((
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !scanning && lookupState !== 'scanning' && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-head-blue">
          <div className="modal-head-blue-fade" />
          <div style={{ position: 'relative', zIndex: 1, width: 48, height: 48, borderRadius: '50%', background: 'var(--surface-card)', border: 'none', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--action-text)' }} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14M21 5v14"/><rect x="1" y="3" width="22" height="18" rx="2"/></svg>
          </div>
          <div className="modal-head-title">Register Barcodes</div>
        </div>
        <div className="modal-body">

          {/* Lookup scan mode: the camera viewfinder */}
          {lookupState === 'scanning' && (
            <>
              <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>Point camera at a barcode to look it up</div>
              <div id="lookup-qr-reader" style={{ width: '100%', borderRadius: 'var(--oh-radius-control)', overflow: 'hidden', background: 'var(--viewfinder)', minHeight: 220, marginBottom: 12 }} />
              <button onClick={() => { stopScan(); setLookupState(null); }}
                style={{ width: '100%', padding: '11px', borderRadius: 'var(--oh-radius-control)', background: 'var(--hover)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel and search instead
              </button>
            </>
          )}

          {/* Lookup not-found screen */}
          {lookupState === 'notfound' && (
            <>
              <div style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', borderRadius: 'var(--oh-radius-control)', padding: '20px 16px', textAlign: 'center', marginBottom: 16 }}>
                <div style={{ width: 32, height: 32, background: 'var(--critical)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', color: 'var(--on-action)', fontSize: 18, fontWeight: 700 }}>!</div>
                <div className="oh-title" style={{ fontSize: 15, fontWeight: 700, color: 'var(--critical-ink)' }}>Barcode not in catalog</div>
                <div style={{ fontSize: 11, color: 'var(--critical-ink)', marginTop: 6, opacity: 0.8, fontFamily: 'monospace' }}>{notFoundBarcode}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={() => { setNotFoundBarcode(''); startLookupScan(); }}
                  style={{ width: '100%', padding: '13px', borderRadius: 'var(--oh-radius-control)', background: 'var(--hover)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Scan Again
                </button>
                <button onClick={() => { setLookupState(null); }}
                  style={{ width: '100%', padding: '13px', borderRadius: 'var(--oh-radius-control)', background: 'var(--action)', border: 'none', color: 'var(--on-action)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Search Catalog Instead
                </button>
              </div>
            </>
          )}

          {/* Normal search-first or edit UI, hidden during lookup states */}
          {!lookupState && (
          <>
          {/* Item search */}
          {!selectedItem ? (
            <div>
              <div className="form-lbl" style={{ marginTop: 0 }}>Search for an item</div>
              <input className="form-inp" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Item number or description..." autoFocus />
              {search.length >= 1 && (
                <div style={{ border: '1.5px solid var(--action)', borderRadius: 'var(--oh-radius-chip)', overflow: 'hidden', marginTop: 6, maxHeight: 280, overflowY: 'auto' }}>
                  {filteredItems.length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No items found</div>
                  ) : filteredItems.map(item => (
                    <div key={item.id} {...pressable(() => { selectItem(item, notFoundBarcode); setNotFoundBarcode(''); })}
                      style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-soft)', cursor: 'pointer', background: 'var(--surface-data)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-data)'}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--action-text)' }}>{item.item_number}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{item.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Selected item header */}
              <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-bg)', borderRadius: 'var(--oh-radius-control)', padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--action-text)' }}>{selectedItem.item_number}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{selectedItem.description}</div>
                </div>
                <button onClick={() => { setSelectedItem(null); setBarcodes(emptySlots()); }}
                  style={{ fontSize: 12, color: 'var(--link)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                  Change
                </button>
              </div>

              {/* Camera viewfinder */}
              {scanning && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Scanning for {scanTarget?.replace('_', ' ')}... point camera at barcode
                  </div>
                  <div id="reg-qr-reader" style={{ width: '100%', borderRadius: 'var(--oh-radius-control)', overflow: 'hidden', background: 'var(--viewfinder)', minHeight: 200 }} />
                  <button onClick={stopScan} style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 'var(--oh-radius-chip)', border: '1.5px solid var(--border)', background: 'var(--hover)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cancel Scan
                  </button>
                </div>
              )}

              {/* Barcode slots */}
              {!scanning && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {BARCODE_FIELDS.map((field, idx) => {
                    const hasExisting = !!selectedItem[field];
                    const currentVal  = barcodes[field];
                    return (
                      <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', width: 64, flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Barcode {idx + 1}
                          {hasExisting && (currentVal
                            ? <span style={{ display: 'block', fontSize: 9, color: 'var(--success-ink)', fontWeight: 700 }}>Saved</span>
                            : <span style={{ display: 'block', fontSize: 9, color: 'var(--critical-ink)', fontWeight: 700 }}>Removed on save</span>)}
                        </div>
                        <input
                          value={currentVal}
                          onChange={e => setBarcodes(prev => ({ ...prev, [field]: e.target.value }))}
                          placeholder={hasExisting ? selectedItem[field] : 'Empty'}
                          style={{ flex: 1, padding: '8px 12px', border: '1.5px solid', borderColor: currentVal ? 'var(--action)' : hasExisting ? 'var(--success)' : 'var(--border-strong)', borderRadius: 'var(--oh-radius-chip)', fontSize: 13, fontFamily: 'inherit', background: currentVal ? 'var(--selected)' : hasExisting ? 'var(--success-bg)' : 'var(--surface-data)', outline: 'none' }}
                        />
                        <button onClick={() => startScan(field)}
                          style={{ padding: '8px 12px', borderRadius: 'var(--oh-radius-chip)', background: 'var(--action)', color: 'var(--on-action)', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                          Scan
                        </button>
                        {currentVal && (
                          <button onClick={() => setBarcodes(prev => ({ ...prev, [field]: '' }))}
                            aria-label={'Remove barcode ' + (idx + 1)}
                            style={{ padding: '8px 10px', borderRadius: 'var(--oh-radius-chip)', background: 'var(--critical-bg)', color: 'var(--critical-ink)', border: '1px solid var(--critical-bg)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                            X
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={() => { stopScan(); onClose(); }}>Close</button>
          {selectedItem && !scanning && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Barcodes'}
            </button>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}


/*
  The Item Catalog.

  **Pointed at the new catalog on 11 October 2026, job 37**, after the owner
  found it empty: *"the item catalog does not show anything, did we never point
  the item catalog menu to point to the new onhand item catalog?"* It had been
  reading the old `items` table, whose 5,000 rows all belong to OnHand Testing.

  **His answer the same day**: it opens on the company's own items, with their
  ClaimSoft and Account Edge numbers, and a switch reaches every vendor item.
  So three tabs: **Our items** (`org_catalog`), **Every vendor item**
  (`catalog_items`, 354,063, shared), and **Product Conversions** (stage 3).

  **Still a page at a time, and the database does the searching**, as job 13
  built it on 6 September; the data is in `src/lib/orgCatalog.js`.

  **Not on it yet, on purpose**: adding an item by hand here, registering a
  barcode, and the old "Waiting on a yes or no" tab, which was the old twin
  matching that Item Mapping replaced. Changing an item's ClaimSoft or Account
  Edge number by hand is the next step. `BarcodeRegisterModal` above still
  works on the old table and is not shown.
*/
export default function AdminItemCatalog({ readOnly = false, mobile = false }) {
  const toast = useToast();
  const { profile } = useAuth();
  const [tab, setTab] = useState('mine');
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [vendor, setVendor] = useState('');
  const [vendors, setVendors] = useState([]);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openId, setOpenId] = useState(null);
  const panelRef = useRef(null);

  /* A quarter of a second of quiet before searching, as before. */
  useEffect(() => {
    const t = setTimeout(() => setSearch(typed), 250);
    return () => clearTimeout(t);
  }, [typed]);

  useEffect(() => {
    loadVendors().then(r => setVendors(r.vendors));
    loadOrgCounts().then(r => { if (!r.error) setCounts(r); });
  }, []);

  const page = useCallback(from => {
    const v = vendors.find(x => x.name === vendor);
    const vendorId = v ? v.id : '';
    return tab === 'every' ? loadVendorPage({ search, vendorId, from }) : loadOrgPage({ search, vendorId, from });
  }, [tab, search, vendor, vendors]);

  useEffect(() => {
    if (tab === 'conversions') return undefined;
    let live = true;
    setRows(null);
    setOpenId(null);
    page(0).then(r => {
      if (!live) return;
      /* A read that fails is not an empty catalog. */
      if (r.error) { setFailed(r.error.message); setRows([]); setTotal(0); return; }
      setFailed(null);
      setRows(r.rows);
      setTotal(r.total);
    });
    return () => { live = false; };
  }, [page, tab]);

  /* After an edit in the item panel: the rows on screen and the figures are
     read again, keeping the item open. */
  async function refresh() {
    const r = await page(0);
    if (!r.error) { setRows(r.rows); setTotal(r.total); }
    loadOrgCounts().then(c => { if (!c.error) setCounts(c); });
  }

  async function more() {
    setLoadingMore(true);
    const r = await page(rows.length);
    if (!r.error) setRows(prev => [...prev, ...r.rows]);
    setLoadingMore(false);
  }

  /* The download holds what the filters matched, not what is drawn. */
  async function exportCurrent() {
    const header = ['OH Number', 'ClaimSoft', 'Account Edge', 'Vendor', 'Vendor Part Number', 'Description', 'Newest Price', 'Per', 'Cost Each', 'Price Book'];
    const body = [];
    for (let from = 0; ; from += 1000) {
      const v = vendors.find(x => x.name === vendor);
      const r = await loadOrgPage({ search, vendorId: v ? v.id : '', from, size: 1000 });
      if (r.error) { toast.error('The export stopped: ' + r.error.message); return; }
      body.push(...r.rows.map(i => [i.oh_number, i.claimsoft_number, i.account_edge_number, i.vendor, i.vendor_part_number,
        i.description, i.price, i.price_unit, i.cost_each == null ? '' : Number(i.cost_each).toFixed(2), i.price_book].map(v => (v == null ? '' : v))));
      if (r.rows.length < 1000) break;
    }
    downloadCsv([header, ...body], 'OnHand_ItemCatalog');
    recordExport(profile, 'EXPORT_CATALOG', body.length);
    toast.success('Exported ' + body.length.toLocaleString() + ' items');
  }

  const open = id => { setOpenId(id); requestAnimationFrame(() => revealPanel(panelRef.current)); };
  const money = n => (n == null ? '' : '$' + Number(n).toFixed(2));

  const mineColumns = [
    { label: 'ClaimSoft', width: '150px' },
    { label: 'Account Edge', width: '140px' },
    { label: 'Description', width: 'minmax(0,1fr)' },
    { label: 'Vendor', width: '200px' },
    { label: 'Cost each', width: '110px', align: 'right' },
  ];
  const everyColumns = [
    { label: 'Vendor part number', width: '190px' },
    { label: 'Description', width: 'minmax(0,1fr)' },
    { label: 'Vendor', width: '220px' },
    { label: 'Ours', width: '90px' },
  ];
  const sub = t => <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{t}</span>;

  return (
    <div>
      <PageBar
        title="Item Catalog"
        subtitle="Our items, and every vendor's"
        twoRows
        subTabs={[
          { key: 'mine', label: 'Our items', count: counts ? counts.items : undefined },
          { key: 'every', label: 'Every vendor item' },
          /* His answer of 11 October 2026: "The product conversions will live
             in the item catalog." Ordering stage 3. */
          { key: 'conversions', label: 'Product Conversions' },
        ]}
        activeSubTab={tab}
        onSubTabChange={setTab}
        controls={tab === 'conversions' ? null : (
          <>
            <SearchInput value={typed} onChange={e => setTyped(e.target.value)}
              placeholder={tab === 'mine' ? 'Search a ClaimSoft, Account Edge or part number, or a description' : 'Search a part number or a description'} />
            <select className={'tb-select' + (vendor ? ' set' : '')} value={vendor} onChange={e => setVendor(e.target.value)}
              aria-label="Which vendor">
              <option value="">All vendors</option>
              {vendors.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
          </>
        )}
        meta={tab === 'conversions' ? undefined
          : rows === null ? 'Reading...'
          : (tab === 'every' ? 'about ' : '') + total.toLocaleString() + ' item' + (total === 1 ? '' : 's')}
        actions={tab === 'mine' && !mobile ? <button className="tb-btn tb-btn-ghost" onClick={exportCurrent}>Export current</button> : null}
      />

      {tab === 'mine' && counts && (
        <div className="rpsum">
          <Figure n={counts.items} label="Our items" />
          <Figure n={counts.claimsoft} label="With a ClaimSoft number" />
          <Figure n={counts.accountEdge} label="With an Account Edge number" />
        </div>
      )}

      {tab === 'conversions' ? (
        <ConversionsTab readOnly={readOnly} />
      ) : (
        <div className="rplist">
          {failed && <p style={{ color: 'var(--critical-ink)' }}>OnHand could not read the catalog just now: {failed}</p>}
          {rows === null ? <PageLoading rows={8} /> : (
            <>
              <div style={openId ? { maxHeight: '40vh', overflowY: 'auto', borderRadius: 'var(--oh-radius-card)' } : undefined}>
              <ListGrid
                layoutKey={tab === 'mine' ? 'item_catalog_ours' : 'item_catalog_every'}
                layoutTitle="The catalog's columns"
                columns={tab === 'mine' ? mineColumns : everyColumns}
                rows={rows}
                keyOf={i => i.catalog_item_id}
                rowClass={i => (i.catalog_item_id === openId ? 'is-on' : '')}
                onRowClick={i => open(i.catalog_item_id)}
                empty={search || vendor ? 'Nothing matches that.' : 'There are no items here yet.'}
                cells={tab === 'mine'
                  ? i => [
                      i.claimsoft_number ? <span className="rpnum">{i.claimsoft_number}</span> : <span style={{ color: 'var(--text-secondary)' }}>none</span>,
                      i.account_edge_number ? <span className="rpnum">{i.account_edge_number}</span> : <span style={{ color: 'var(--text-secondary)' }}>none</span>,
                      <span className="rpdesc">{i.description}{sub([i.oh_number, i.vendor_part_number].filter(Boolean).join(' · '))}</span>,
                      <span className="rpvend">{i.vendor || 'No vendor'}</span>,
                      i.cost_each != null ? money(i.cost_each)
                        : i.price != null ? <span title={'The newest price book (' + i.price_book + ') does not say what ' + money(i.price) + ' is per'} style={{ color: 'var(--text-secondary)' }}>{money(i.price)} per ?</span>
                        : '',
                    ]
                  : i => [
                      <span className="rpnum">{i.vendor_part_number}</span>,
                      <span className="rpdesc">{i.description}{sub(i.oh_number)}</span>,
                      <span className="rpvend">{i.vendor || 'No vendor'}</span>,
                      i.isMine ? <Pill tone="app">Ours</Pill> : '',
                    ]}
                stack={i => (
                  <span>
                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span className="rpnum">{tab === 'mine' ? (i.claimsoft_number || i.vendor_part_number) : i.vendor_part_number}</span>
                      <span className="rpvend">{i.vendor || ''}</span>
                    </span>
                    <span style={{ display: 'block', fontSize: 13, marginTop: 3 }}>{i.description}</span>
                    {tab === 'mine' && i.account_edge_number && sub('Account Edge ' + i.account_edge_number)}
                  </span>
                )}
              />
              </div>
              {rows.length < total && !openId && (
                <div className="rpmore">
                  <button className="btn btn-quiet" onClick={more} disabled={loadingMore}>
                    {loadingMore ? 'Reading...' : 'Show ' + Math.min(PAGE, total - rows.length).toLocaleString() + ' more'}
                  </button>
                </div>
              )}
            </>
          )}
          <div ref={panelRef}>
            {openId && <ItemPanel key={openId} catalogItemId={openId} readOnly={readOnly} onChanged={refresh} onClose={() => setOpenId(null)} />}
          </div>
        </div>
      )}
    </div>
  );
}

function Figure({ n, label, red }) {
  return (
    <span className="rpsum-c">
      <b className={'rpsum-n' + (red ? ' red' : '')}>{(n || 0).toLocaleString()}</b>
      <span className="rpsum-l">{label}</span>
    </span>
  );
}
